import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Search } from "lucide-react";
import { BrandSearchBar } from "../components/BrandSearchBar";
import { api, SITE_NAME, SITE_URL } from "../lib/api";
import { US_HQ } from "../lib/regions";
import { SEO, faqJsonLd } from "../components/SEO";
import { TYPE_IMAGES, TYPE_IMAGES_SECONDARY } from "../lib/typeImages";
import { Layout } from "../components/Layout";
import { usePartTypeMeta } from "../lib/usePartTypes";
import { splitTerm } from "../lib/localizeCatalog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { lp, lpFull } from "../lib/urls";
import { HOME_EXTRA, HOME_APP_TILES } from "../lib/homeExtra";
import { useFinalDrives } from "../components/FinalDrivesInterstitial";

const FD_SLUG = "final-drive";

const IMG = {
  warehouseAisle: "/company/warehouse-aisle-hero.webp",
};
const HERO_IMG = IMG.warehouseAisle;

const PART_TYPES = [
  { label: "Track rollers", slug: "track-roller" },
  { label: "Carrier rollers", slug: "carrier-roller" },
  { label: "Idlers", slug: "idler" },
  { label: "Sprockets & segments", slug: "sprocket" },
  { label: "Track chains", slug: "track-chain" },
  { label: "Track shoes", slug: "track-shoe" },
  { label: "Track groups", slug: "track-group" },
  { label: "Rubber tracks", slug: "rubber-track" },
  { label: "Rubber pads", slug: "rubber-pad" },
  { label: "Track bolts & nuts", slug: "track-bolt-nut" },
];

const FAQS = [
  {
    q: "Which machinery brands do you cover?",
    a: "We stock undercarriage parts for 350+ brands including Caterpillar, Komatsu, Kobelco, Hitachi, Volvo, JCB, Hyundai, Doosan, Case, Liebherr, Takeuchi, Yanmar, Kubota and many more.",
  },
  {
    q: "How do I find parts for my machine?",
    a: "Select your machinery brand, then pick your exact model from the alphabetical list. Every model page lists all available undercarriage components: rollers, sprocket segments, track chains, shoes, bolts and nuts.",
  },
  {
    q: "How do I get pricing?",
    a: "Open any part page and submit the Request a Quote form. Our team responds with pricing and availability.",
  },
  {
    q: "Are the parts OEM quality?",
    a: "All parts are manufactured to OEM specifications from boron and alloy steels, with deep-hardened wear surfaces and sealed, lifetime-lubricated assemblies where applicable.",
  },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const hx = HOME_EXTRA[(i18n.language || "en").slice(0, 2)] || HOME_EXTRA.en;
  const lang = (i18n.language || "en").slice(0, 2);
  const ptMeta = usePartTypeMeta();
  const [stats, setStats] = useState({
    brands: 357,
    models: 9643,
    parts: 48784,
  });
  const [brands, setBrands] = useState(null);
  const [brandsError, setBrandsError] = useState(false);
  const [brandQ, setBrandQ] = useState("");
  const filteredBrands = brands
    ? brandQ
      ? brands.filter((b) => b.name.toLowerCase().includes(brandQ.toLowerCase()))
      : brands
    : null;
  const featuredBrands = brands
    ? [...brands]
        .sort((a, b) => (b.model_count || 0) - (a.model_count || 0))
        .slice(0, 36)
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const [typeCounts, setTypeCounts] = useState({});
  useEffect(() => {
    const srcs = [
      ...new Set([
        ...Object.values(TYPE_IMAGES),
        ...Object.values(TYPE_IMAGES_SECONDARY).map((s) => s.src),
      ]),
    ];
    srcs.forEach((s) => {
      const im = new Image();
      im.src = s;
    });
  }, []);
  useEffect(() => {
    api
      .get("/part-types")
      .then((r) => {
        const m = {};
        (r.data || []).forEach((x) => {
          m[x.slug] = x.count;
        });
        setTypeCounts(m);
      })
      .catch(() => {});
  }, []);
  const [q, setQ] = useState("");
  const [activeType, setActiveType] = useState(PART_TYPES[0].slug);
  const { openFinalDrives } = useFinalDrives();
  const navigate = useNavigate();

  const loadBrands = () => {
    setBrandsError(false);
    setBrands(null);
    api
      .get("/brands")
      .then((r) => setBrands(r.data))
      .catch(() => {
        setBrands([]);
        setBrandsError(true);
      });
  };

  useEffect(() => {
    api
      .get("/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
    loadBrands();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(lp(`/search?q=${encodeURIComponent(q.trim())}`));
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      parentOrganization: {
        "@type": "Organization",
        name: "VANROCK Group",
        url: "https://vanrockglobal.com",
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: US_HQ.phone,
        contactType: "sales",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}${lpFull("/search")}?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    faqJsonLd(FAQS),
  ];

  return (
    <Layout>
      <SEO
        title={t("seo.homeTitle")}
        description={t("seo.homeDesc")}
        path="/"
        jsonLd={jsonLd}
      />
      <section className="grid lg:grid-cols-12 border-b border-border bg-white">
        <div className="lg:col-span-6 lg:order-2 p-6 sm:p-14 flex flex-col justify-center lg:items-center border-b lg:border-b-0 border-border">
          <div className="w-full lg:max-w-2xl">
          <div className="w-12 h-[3px] bg-yellow-500 mb-7" aria-hidden="true" />
          <h1 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter text-gray-950 break-words">
            {t("home.h1a")}
            <br />
            <span className="text-gray-950 box-decoration-clone" data-testid="hero-highlight">
              {t("home.h1b")}
              <br />
              {t("home.h1c")}
            </span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-gray-600 max-w-xl">
            {t("home.heroSub", {
              brands: stats.brands,
              models: stats.models.toLocaleString(),
            })}
          </p>
          <form
            onSubmit={submit}
            className="mt-10 flex max-w-xl border border-gray-300 bg-white shadow-[0_18px_40px_-18px_rgba(10,10,10,0.25)] focus-within:border-gray-950 transition-colors"
          >
            <input
              data-testid="hero-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("nav.searchPlaceholder")}
              className="flex-1 bg-transparent px-5 py-4 outline-none placeholder:text-gray-400 text-xs font-mono-tech text-gray-950"
            />
            <button
              data-testid="hero-search-submit"
              type="submit"
              className="bg-[#0A0A0A] text-white font-mono-tech text-xs tracking-[0.2em] uppercase px-6 sm:px-8 flex items-center gap-2 hover:bg-yellow-500 hover:text-black transition-colors"
            >
              <Search size={15} />{" "}
              <span className="hidden sm:inline">{t("home.searchBtn")}</span>
            </button>
          </form>
          <div className="mt-6 flex flex-wrap gap-2 items-center">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-gray-400">
              {t("home.try")}
            </span>
            {["SK210", "PC200", "EX120", "320D"].map((t) => (
              <button
                key={t}
                onClick={() => navigate(lp(`/search?q=${t}`))}
                data-testid={`quick-search-${t}`}
                className="font-mono-tech text-[11px] px-3 py-2 border border-border bg-gray-50 text-gray-700 hover:border-gray-950 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {t}
              </button>
            ))}
          </div>
          </div>
        </div>
        <div className="lg:col-span-6 lg:order-1 relative min-h-[340px] lg:border-r border-border">
          <img
            src={IMG.warehouseAisle}
            alt={t("imgAlt.warehouseStock")}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-[#0A0A0A] px-5 py-3 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-yellow-500 flex justify-end">
            <span>{t("home2.brandsIndexed", { n: stats.brands })}</span>
          </div>
        </div>
      </section>

      <Link
        to={lp("/newsroom")}
        data-testid="home-news-ticker"
        className="group flex items-center gap-4 sm:gap-5 bg-[#0A0A0A] border-b border-border px-6 sm:px-14 py-5 sm:py-6 hover:bg-gray-900 transition-colors"
      >
        <span className="shrink-0 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-black bg-yellow-500 px-2.5 py-1">
          {t("news.tabNews")}
        </span>
        <p className="text-sm sm:text-base font-medium text-white truncate">{t("news.tickerT")}</p>
        <span className="ml-auto shrink-0 text-xs sm:text-sm font-mono-tech uppercase tracking-[0.15em] text-yellow-500 group-hover:translate-x-1 transition-transform">
          {t("news.tickerCta")} →
        </span>
      </Link>

      <section
        className="border-b border-border bg-white p-6 sm:p-14"
        data-testid="home-components-section"
      >
        <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">
          {t("home.compKicker")}
        </p>
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter mb-8 text-gray-950">
          {t("home.compTitle")}
        </h2>
      <div className="grid grid-cols-3 bg-neutral-50 border-y border-border -mx-6 sm:-mx-14 mb-8">
        <div
          className="group p-6 sm:p-10 sm:pl-10 lg:pl-14 border-r border-border relative overflow-hidden"
          data-testid="stat-brands"
        >
          <div className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-gray-950">
            {stats.brands}
          </div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">
            {t("home.statBrands")}
          </div>
          <span className="absolute top-4 right-4 font-mono-tech text-[10px] text-yellow-600/70">01</span>
        </div>
        <div
          className="group p-6 sm:p-10 border-r border-border relative overflow-hidden"
          data-testid="stat-models"
        >
          <div className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-gray-950">
            {stats.models.toLocaleString()}
          </div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">
            {t("home.statModels")}
          </div>
          <span className="absolute top-4 right-4 font-mono-tech text-[10px] text-yellow-600/70">02</span>
        </div>
        <div className="group p-6 sm:p-10 relative overflow-hidden" data-testid="stat-parts">
          <div className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-gray-950">
            {stats.parts.toLocaleString()}
          </div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500 mt-2">
            {t("home.statParts")}
          </div>
          <span className="absolute top-4 right-4 font-mono-tech text-[10px] text-yellow-600/70">03</span>
        </div>
      </div>

        <div
          className="relative border border-border mb-8 bg-white hidden sm:block"
          data-testid="home-undercarriage-figure"
        >
          <img
            src="/components/undercarriage-assembly.webp"
            alt={t("imgAlt.undercarriageSystem")}
            className="mx-auto max-h-[260px] lg:max-h-[380px] w-auto object-contain py-6"
            loading="lazy"
          />
          <div className="absolute bottom-0 left-0 right-0 flex justify-end px-4 py-2 font-mono-tech text-[9px] uppercase tracking-[0.25em]">
            <span className="hidden lg:block text-yellow-600">VANROCK</span>
          </div>
        </div>
        <div
          className="flex flex-wrap border-b border-border"
          role="tablist"
          data-testid="home-comp-tabs"
        >
          {PART_TYPES.map((pt, i) => {
            const term = splitTerm(ptMeta(pt.slug)?.name || pt.label);
            const active = activeType === pt.slug;
            return (
              <button
                key={pt.slug}
                role="tab"
                aria-selected={active}
                onClick={() => setActiveType(pt.slug)}
                data-testid={`home-comp-tab-${pt.slug}`}
                className={`font-mono-tech text-xs font-bold uppercase tracking-[0.15em] px-4 py-3.5 -mb-px border-b-2 transition-colors ${
                  active
                    ? "border-yellow-500 text-gray-950 bg-yellow-500/10"
                    : "border-transparent text-gray-700 hover:text-gray-950 hover:bg-gray-50"
                }`}
              >
                <span
                  className={`mr-1.5 ${active ? "text-yellow-600" : "text-gray-400"}`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {term.local}
              </button>
            );
          })}
          <button
            role="tab"
            aria-selected={activeType === FD_SLUG}
            onClick={() => setActiveType(FD_SLUG)}
            data-testid="home-comp-tab-final-drive"
            className={`font-mono-tech text-xs font-bold uppercase tracking-[0.15em] px-4 py-3.5 -mb-px border-b-2 transition-colors ${
              activeType === FD_SLUG
                ? "border-yellow-500 text-gray-950 bg-yellow-500/10"
                : "border-transparent text-gray-700 hover:text-gray-950 hover:bg-gray-50"
            }`}
          >
            <span className={`mr-1.5 ${activeType === FD_SLUG ? "text-yellow-600" : "text-gray-400"}`}>
              {String(PART_TYPES.length + 1).padStart(2, "0")}
            </span>
            {t("fd.navLabel", "Final Drives")}
          </button>
        </div>
        {(() => {
          if (activeType === FD_SLUG) {
            return (
              <div className="grid lg:grid-cols-12 border-x border-b border-border" data-testid="home-comp-panel-final-drive">
                <div className="lg:col-span-8 p-6 sm:p-10 lg:border-r border-b lg:border-b-0 border-border flex flex-col md:flex-row gap-8">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl text-gray-950 leading-tight">
                      {t("fd.navLabel", "Final Drives")}
                    </h3>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-400 mt-1">
                      TRAVEL MOTORS
                    </p>
                    <p className="text-sm text-gray-600 mt-4 max-w-2xl leading-relaxed">
                      {t("fd.body", "Final drives live on our dedicated online store, VANROCK Drives — with instant stock availability, quotes, parts lists and worldwide shipping across thousands of machine models.")}
                    </p>
                  </div>
                  <div className="flex items-center justify-center w-full md:w-[240px] xl:w-[300px] shrink-0">
                    <img
                      src="/components/final-drive.webp"
                      alt={t("imgAlt.finalDrive")}
                      data-testid="home-comp-image-final-drive"
                      className="max-h-[200px] w-auto object-contain"
                    />
                  </div>
                </div>
                <div className="lg:col-span-4 flex flex-col">
                  <div className="p-6 sm:p-8 border-b border-border">
                    <div className="font-display font-black uppercase tracking-tighter text-2xl text-gray-950 leading-none">
                      {t("fd.headlineShort", "World's largest range")}
                    </div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-400 mt-2">
                      {t("fd.online", "Online store")}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={openFinalDrives}
                    data-testid="home-comp-view-final-drive"
                    className="flex-1 flex items-center justify-between px-6 sm:px-8 py-6 font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950 hover:bg-yellow-500 transition-colors"
                  >
                    {t("fd.visit", "Visit store")}
                    <span aria-hidden="true">↗</span>
                  </button>
                </div>
              </div>
            );
          }
          const pt =
            PART_TYPES.find((p) => p.slug === activeType) || PART_TYPES[0];
          const meta = ptMeta(pt.slug);
          const term = splitTerm(meta?.name || pt.label);
          const count = typeCounts[pt.slug];
          return (
            <div
              className="grid lg:grid-cols-12 border-x border-b border-border"
              data-testid="home-comp-panel"
            >
              <div className="lg:col-span-8 p-6 sm:p-10 lg:border-r border-b lg:border-b-0 border-border flex flex-col md:flex-row gap-8">
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-black uppercase tracking-tighter text-2xl sm:text-3xl text-gray-950 leading-tight">
                    {term.local}
                  </h3>
                  {term.en && (
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-400 mt-1">
                      {term.en}
                    </p>
                  )}
                  {meta?.aka && (
                    <p className="font-mono-tech text-[11px] text-gray-500 mt-4">
                      {meta.aka}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-4 max-w-2xl leading-relaxed">
                    {t(`home.compDesc.${pt.slug}`)}
                  </p>
                </div>
                <div className="flex flex-col items-center justify-center gap-3 w-full md:w-[240px] xl:w-[300px] shrink-0">
                  {PART_TYPES.map((p2) => {
                    const src = TYPE_IMAGES[p2.slug];
                    if (!src) return null;
                    const sec = TYPE_IMAGES_SECONDARY[p2.slug];
                    const on = p2.slug === pt.slug;
                    return (
                      <div
                        key={p2.slug}
                        className={
                          on ? "flex flex-col items-center gap-3" : "hidden"
                        }
                      >
                        <img
                          src={src}
                          alt={splitTerm(ptMeta(p2.slug)?.name || p2.label).local}
                          data-testid={`home-comp-image-${p2.slug}`}
                          className={`${sec ? "max-h-[150px]" : "max-h-[200px]"} w-auto object-contain`}
                        />
                        {sec && (
                          <div className="flex items-center gap-2 border-t border-border pt-3">
                            <img
                              src={sec.src}
                              alt={sec.label}
                              data-testid={`home-comp-image-secondary-${p2.slug}`}
                              className="max-h-[64px] w-auto object-contain"
                            />
                            <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-gray-400">
                              {sec.label}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="lg:col-span-4 flex flex-col">
                <div className="p-6 sm:p-8 border-b border-border">
                  <div className="font-mono-tech font-bold text-3xl sm:text-4xl text-gray-950">
                    {count != null ? count.toLocaleString() : "—"}
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-400 mt-1">
                    {t("home.statParts")}
                  </div>
                </div>
                <Link
                  to={lp(`/parts/${pt.slug}`)}
                  data-testid={`home-comp-view-${pt.slug}`}
                  className="flex-1 flex items-center justify-between px-6 sm:px-8 py-6 font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950 hover:bg-yellow-500 transition-colors"
                >
                  {t("home.compView")}
                </Link>
              </div>
            </div>
          );
        })()}
      </section>

      <section className="border-b border-border">
        <div className="flex items-stretch justify-between border-b border-border bg-white">
          <div className="p-6 sm:px-14 sm:py-8">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">
              {t("home.browseKicker")}
            </p>
            <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tighter text-gray-950">
              {t("home.brandIndex")}
            </h2>
          </div>
          <Link
            to={lp("/brands")}
            data-testid="home-all-brands-link"
            className="cell-hover border-l border-border px-6 sm:px-10 flex items-center font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950"
          >
            {t("home.allN", { n: brands?.length || 357 })}
          </Link>
        </div>
        <div className="bg-white border-b border-border px-6 sm:px-14 py-5">
          <BrandSearchBar value={brandQ} onChange={setBrandQ} placeholder={t("brandsHub.search")}
            count={filteredBrands?.length ?? 0} testid="home-brand-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {brands === null &&
            Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-white border-r border-b border-border p-5 min-h-[100px] animate-pulse"
              >
                <div className="h-3 w-8 bg-gray-100 mb-6" />
                <div className="h-4 w-3/4 bg-gray-200" />
              </div>
            ))}
          {brandsError && (
            <div
              className="col-span-full bg-white p-10 text-center"
              data-testid="home-brands-load-error"
            >
              <p className="text-gray-600 mb-4">{t("brandsHub.loadError")}</p>
              <button
                onClick={loadBrands}
                data-testid="home-brands-retry-button"
                className="bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black font-mono-tech text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors"
              >
                {t("brandsHub.retry")}
              </button>
            </div>
          )}
          {brands && filteredBrands.length === 0 && (
            <p className="col-span-full bg-white p-10 text-center text-sm text-gray-500" data-testid="home-brands-no-match">
              {t("brandsHub.noMatch")}
            </p>
          )}
        </div>
        {brands && brandQ.trim() !== "" && filteredBrands.length > 0 && (
          <div className="bg-white px-6 sm:px-14 py-8 flex flex-wrap gap-2" data-testid="home-brands-search-results">
            {filteredBrands.map((b) => (
              <Link
                key={b.slug}
                to={lp(`/brand/${b.slug}`)}
                data-testid={`brand-card-${b.slug}`}
                className="font-mono-tech text-[11px] uppercase tracking-wide px-3 py-2 border border-border bg-white text-gray-800 hover:border-gray-950 hover:bg-[#0A0A0A] hover:text-white transition-colors"
              >
                {b.name}
                <span className="ml-2 text-gray-400">{b.model_count}</span>
              </Link>
            ))}
          </div>
        )}
        {brands && brandQ.trim() === "" && (
          <>
            <div className="bg-white px-6 sm:px-14 pt-7 pb-4">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-gray-500">
                <span className="inline-block w-6 h-[2px] bg-yellow-500 align-middle mr-3" aria-hidden="true" />
                {t("home.topBrands")}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-y border-border bg-white" data-testid="home-featured-brands">
              {featuredBrands.map((b, i) => (
                <Link
                  key={b.slug}
                  to={lp(`/brand/${b.slug}`)}
                  data-testid={`brand-card-${b.slug}`}
                  className="cell-hover bg-white border-r border-b [&:nth-child(n+35)]:border-b-0 md:[&:nth-child(n+34)]:border-b-0 lg:[&:nth-child(n+31)]:border-b-0 border-border p-4 sm:p-5 min-h-[104px] flex flex-col justify-between group"
                >
                  <span className="font-mono-tech text-[10px] text-gray-400 group-hover:text-yellow-600 transition-colors">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="font-display font-bold uppercase text-sm leading-tight text-gray-950">
                      {b.name}
                    </div>
                    <div className="font-mono-tech text-[10px] text-gray-500 mt-1">
                      {b.model_count} {t("home.modelsWord")}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="bg-neutral-50 px-6 sm:px-14 py-10 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-testid="home-brands-az">
              <Link
                to={lp("/brands")}
                data-testid="home-brands-az-all-link"
                className="inline-flex items-center justify-center gap-3 bg-yellow-500 hover:bg-[#0A0A0A] hover:text-yellow-500 text-black font-mono-tech text-xs uppercase tracking-[0.2em] px-8 py-4 transition-colors"
              >
                {t("home.browseAz", { n: brands.length })}
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </>
        )}
      </section>

      <section className="border-b border-border bg-white pb-10 sm:pb-16" data-testid="home-applications-strip">
        <div className="flex flex-col sm:flex-row sm:items-stretch sm:justify-between border-b border-border">
          <div className="p-6 sm:px-14 sm:py-10">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-3">{hx.appsKicker}</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter text-gray-950">{hx.appsTitle}</h2>
          </div>
          <Link
            to={lp("/applications")}
            data-testid="home-all-applications-link"
            className="cell-hover border-t sm:border-t-0 sm:border-l border-border px-6 sm:px-12 py-4 sm:py-0 flex items-center gap-3 font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950"
          >
            {hx.appsCta} →
          </Link>
        </div>
        <div className="grid sm:grid-cols-3">
          {HOME_APP_TILES.slice(0, 3).map((a) => (
            <Link
              key={a.n}
              to={`${lp("/applications")}#${a.img}`}
              data-testid={`home-app-tile-${a.img}`}
              className="relative group h-[260px] sm:h-[340px] border-r border-b border-border overflow-hidden"
            >
              <img
                src={`/applications/${a.img}.webp`}
                alt={t(`apps.a${a.n}t`)}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-gray-950/85 via-gray-950/25 to-gray-950/10 group-hover:via-gray-950/10 transition-colors duration-500" />
              <span className="absolute top-4 left-5 font-mono-tech text-[11px] text-yellow-500">0{a.n}</span>
              <span className="absolute bottom-5 left-5 right-4">
                <span className="block font-display font-black uppercase text-xl sm:text-2xl text-white tracking-tight leading-none">
                  {t(`apps.a${a.n}t`)}
                </span>
                <span className="mt-2 block h-[2px] w-8 bg-yellow-500 group-hover:w-16 transition-all duration-500" />
              </span>
            </Link>
          ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4">
          {HOME_APP_TILES.slice(3).map((a) => (
            <Link
              key={a.n}
              to={`${lp("/applications")}#${a.img}`}
              data-testid={`home-app-tile-${a.img}`}
              className="relative group h-[200px] sm:h-[240px] border-r border-b border-border overflow-hidden"
            >
              <img
                src={`/applications/${a.img}.webp`}
                alt={t(`apps.a${a.n}t`)}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-gray-950/85 via-gray-950/25 to-gray-950/10 group-hover:via-gray-950/10 transition-colors duration-500" />
              <span className="absolute top-4 left-5 font-mono-tech text-[11px] text-yellow-500">0{a.n}</span>
              <span className="absolute bottom-5 left-5 right-4">
                <span className="block font-display font-black uppercase text-lg sm:text-xl text-white tracking-tight leading-none">
                  {t(`apps.a${a.n}t`)}
                </span>
                <span className="mt-2 block h-[2px] w-8 bg-yellow-500 group-hover:w-16 transition-all duration-500" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="home-company-synopsis">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[320px] lg:min-h-0 border-b lg:border-b-0 lg:border-r border-border">
            <img
              src="/vanrock-facility.webp"
              alt={t("imgAlt.facility")}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="px-6 sm:px-14 py-14 sm:py-20 flex flex-col justify-center">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-6">{hx.kicker}</p>
            <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tighter leading-[0.95] text-gray-950 max-w-xl">
              {hx.title}
            </h2>
            <p className="mt-6 text-sm text-gray-700 leading-relaxed max-w-xl">{hx.p1}</p>
            <p className="mt-3 text-sm text-gray-700 leading-relaxed max-w-xl">{hx.p2}</p>
            <p className="mt-3 text-sm text-gray-700 leading-relaxed max-w-xl">
              {hx.p3}{" "}
              <a
                href="https://vanrockglobal.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-synopsis-global-link"
                className="text-yellow-600 hover:text-gray-950 transition-colors whitespace-nowrap"
              >
                vanrockglobal.com ↗
              </a>
            </p>
            <Link
              to={lp("/company")}
              data-testid="home-company-link"
              className="mt-8 inline-flex items-center gap-2 self-start font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950 border-b-2 border-yellow-500 pb-1 hover:text-yellow-600 transition-colors"
            >
              {hx.cta} →
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-white" data-testid="home-mvv-section">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[380px] lg:min-h-0 order-1 lg:order-2 border-b lg:border-b-0 lg:border-l border-border">
            <img
              src="/vanrock-team-duo-2.webp"
              alt={t("imgAlt.people")}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center top" }}
              loading="lazy"
            />
          </div>
          <div className="px-6 sm:px-14 py-16 sm:py-24 flex flex-col justify-center order-2 lg:order-1">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-6">
              {t("mvv.kicker")}
            </p>
            <h2 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.92] text-gray-950 max-w-xl">
              {t("mvv.title")}
            </h2>
            <div className="mt-10 max-w-xl">
              {[
                { k: t("mvv.visionK"), b: t("mvv.visionBody") },
                { k: t("mvv.missionK"), b: t("mvv.missionBody") },
                { k: t("mvv.valuesK"), b: [t("mvv.v1t"), t("mvv.v2t"), t("mvv.v3t")].join(" · ") },
              ].map((row) => (
                <div key={row.k} className="border-t border-border py-5 grid sm:grid-cols-12 gap-2 sm:gap-6">
                  <p className="sm:col-span-4 font-mono-tech text-[10px] tracking-[0.25em] uppercase text-yellow-600 pt-1">{row.k}</p>
                  <p className="sm:col-span-8 text-sm text-gray-700 leading-relaxed">{row.b}</p>
                </div>
              ))}
            </div>
            <Link
              to={lp("/mission-vision-values")}
              data-testid="home-mvv-link"
              className="mt-8 inline-flex items-center gap-2 self-start font-mono-tech text-xs uppercase tracking-[0.2em] text-gray-950 border-b-2 border-yellow-500 pb-1 hover:text-yellow-600 transition-colors"
            >
              {t("mvv.title")} →
            </Link>
          </div>
        </div>
      </section>

      <section
        className="bg-[#0A0A0A] text-white border-b-2 border-yellow-500"
        data-testid="home-dealer-section"
      >
        <div className="grid lg:grid-cols-12">
          <div className="lg:col-span-8 p-6 sm:p-14">
            <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-yellow-500 mb-4">
              {t("home.dealerKicker")}
            </p>
            <h2 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.95]">
              {t("home.dealerT1")} VANROCK {t("home.dealerT2")}
            </h2>
            <p className="mt-5 text-gray-400 text-sm md:text-base max-w-2xl">
              {t("home.dealerSub")}
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-gray-300">
              <li>{t("home.dp1")}</li>
              <li>{t("home.dp2")}</li>
              <li>{t("home.dp3")}</li>
              <li>{t("home.dp4")}</li>
            </ul>
          </div>
          <div className="lg:col-span-4 flex">
            <Link
              to={lp("/become-a-dealer")}
              data-testid="become-dealer-button"
              className="flex-1 flex items-center justify-center gap-3 bg-yellow-500 text-black font-mono-tech text-sm font-bold uppercase tracking-[0.2em] p-8 hover:bg-[#0A0A0A] hover:text-white transition-colors"
            >
              {t("home.dealerCta")}
            </Link>
          </div>
        </div>
      </section>

      <section className="p-6 sm:p-14 border-b border-border bg-white">
        <p className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-gray-500 mb-2">
          {t("home.faqKicker")}
        </p>
        <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tighter mb-8 text-gray-950">
          {t("home.faqTitle")}
        </h2>
        <div className="max-w-4xl">
          <Accordion type="single" collapsible data-testid="home-faq-accordion">
            {[1, 2, 3, 4].map((i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-border"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-950 hover:no-underline">
                  {t(`home.faq${i}q`)}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {t(`home.faq${i}a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </Layout>
  );
}
